import { useEffect, useState } from 'react';
import { messagingApi } from '../../services/messaging-api';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { showToast } from '../../utils/toast';

type MessageBatchesPageProps = {
  tenantId?: string;
  onNavigate?: (page: string) => void;
};

export function MessageBatchesPage({ tenantId = '', onNavigate }: MessageBatchesPageProps) {
  const [batches, setBatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'draft' | 'completed'>('draft');
  const [deleteCandidate, setDeleteCandidate] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadBatches();
  }, [tenantId]);

  const loadBatches = async () => {
    try {
      const response = await messagingApi.getBatches(tenantId);
      setBatches(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const draftBatches = batches.filter((b) => b.status === 'DRAFT');
  const completedBatches = batches.filter((b) => b.status !== 'DRAFT');

  const handleDelete = (id: string, name: string) => {
    setDeleteCandidate({ id, name });
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    try {
      await messagingApi.deleteBatch(deleteCandidate.id);
      await loadBatches();
      setDeleteCandidate(null);
    } catch (err) {
      setError('Failed to delete batch');
      setDeleteCandidate(null);
    }
  };

  const handleReloadRecipients = async (batchId: string) => {
    try {
      await messagingApi.loadRecipients(batchId, {});
      await loadBatches();
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to reload recipients');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      DRAFT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      QUEUED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      SENDING: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-800';
  };

  const displayBatches = activeTab === 'draft' ? draftBatches : completedBatches;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Message Batches</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage messaging batches, track delivery status, and access sent messages.</p>
      </div>

      {/* Header with Tabs and Create Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('draft')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'draft'
                ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-300'
            }`}
          >
            Drafts ({draftBatches.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'completed'
                ? 'text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400'
                : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-gray-300'
            }`}
          >
            Sent & Completed ({completedBatches.length})
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onNavigate?.('providers')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium"
          >
            Providers
          </button>
          <button
            onClick={() => onNavigate?.('create-batch')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            + Create Batch
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-2xl bg-red-100 px-4 py-3 text-red-700 dark:bg-red-900/40 dark:text-red-200 border border-red-400 dark:border-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-700 dark:text-gray-200">Loading...</div>
      ) : displayBatches.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 mb-4">
            {activeTab === 'draft' ? 'No draft batches yet' : 'No completed batches yet'}
          </div>
          {activeTab === 'draft' && (
            <button
              onClick={() => onNavigate?.('create-batch')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Create Your First Batch
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1">
          {displayBatches.map((batch) => (
            <div
              key={batch.id}
              className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {batch.name}
                    </h3>
                    <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${getStatusColor(batch.status)}`}>
                      {batch.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">Channel:</span> {batch.channel}
                    </div>
                    <div>
                      <span className="font-medium">Recipients:</span> {batch._count?.recipients || 0}
                    </div>
                    <div>
                      <span className="font-medium">Provider:</span> {batch.provider?.name || 'Unknown'}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {batch.status === 'DRAFT' ? (
                    <>
                      <button
                        onClick={() => {
                          sessionStorage.setItem('editBatchId', batch.id);
                          onNavigate?.('create-batch');
                        }}
                        className="bg-indigo-500 text-white px-3 py-2 rounded text-sm hover:bg-indigo-600 font-medium"
                        title="View batch details"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(batch.id, batch.name)}
                        className="bg-red-500 text-white px-3 py-2 rounded text-sm hover:bg-red-600"
                        title="Delete batch"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => showToast.info('View details coming soon')}
                        className="bg-gray-600 text-white px-3 py-2 rounded text-sm hover:bg-gray-700 font-medium"
                        title="View batch details and sent messages"
                      >
                        View Details
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {deleteCandidate && (
        <ConfirmationModal
          title="Delete Batch"
          message={`Are you sure you want to delete the batch \"${deleteCandidate.name}\"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          onConfirm={confirmDelete}
          onClose={() => setDeleteCandidate(null)}
        />
      )}
    </div>
  );
}